import { useState, useCallback, useEffect } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config';

function getAuthHeader() {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/**
 * Manages follow/unfollow state for a single analyst.
 *
 * @param {string} slug - analyst slug
 * @param {boolean} initialFollowing - is_following value from the API response
 *
 * Returns:
 *   following  — current follow state
 *   toggle     — call to follow or unfollow
 *   loading    — true while request is in flight
 *   error      — error message string or null
 *   clearError — reset error
 */
export default function useFollowAnalyst(slug, initialFollowing = false) {
  const [following, setFollowing] = useState(initialFollowing);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Sync when the API data arrives (initialFollowing changes from false → true)
  // Only sync while not in the middle of a user-triggered toggle
  useEffect(() => {
    if (!loading) setFollowing(initialFollowing);
  }, [initialFollowing]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = useCallback(async () => {
    if (loading) return;
    setLoading(true);
    setError(null);

    const wasFollowing = following;
    setFollowing(!wasFollowing); // optimistic update

    try {
      const endpoint = wasFollowing ? 'unfollow' : 'follow';
      await axios.post(
        `${API_BASE_URL}/api/research/analysts/${slug}/${endpoint}/`,
        {},
        { headers: getAuthHeader() },
      );
    } catch (err) {
      setFollowing(wasFollowing); // revert on error
      setError(err.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [slug, following, loading]);

  const clearError = useCallback(() => setError(null), []);

  return { following, toggle, loading, error, clearError };
}
